import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import Spinner from "../../Shared/Spinner";
import { useLinkedInCardsRanking, useLinkedInCardsByFilter, useLinkedInCardsFilterOptions } from "../../../hooks/use-query-linkedincards";

// Sub-componente para exibir o drill-down de vagas agrupadas ao expandir a linha
const LinkedinCardsGroupDetails: React.FC<{ rowData: any; activeDimensions: string[] }> = ({
  rowData,
  activeDimensions,
}) => {
  const filters: any = {};
  activeDimensions.forEach((dimension) => {
    if (rowData[dimension] !== undefined) {
      filters[dimension] = rowData[dimension];
    }
  });

  // Forçar limite de 100 para o detalhamento da linha expandida
  const { data: cardsRes, isLoading } = useLinkedInCardsByFilter({
    ...filters,
    limit: 100,
    page: 1,
  });

  const cardsData = cardsRes?.data || [];

  if (isLoading) return <Spinner />;

  const linkTemplate = (rowData: any) => {
    return (
      <a href={rowData.cardLink} target="_blank" rel="noopener noreferrer" className="p-button-text font-bold text-blue-600 flex align-items-center">
        Ver Vaga <i className="pi pi-external-link ml-1" />
      </a>
    );
  };

  return (
    <div className="p-3 border-round surface-100">
      <h5>Detalhamento das Vagas</h5>
      <DataTable value={cardsData} className="p-datatable-sm" emptyMessage="Nenhum detalhe disponível.">
        <Column field="title" header="Título da Vaga" sortable />
        <Column field="companyName" header="Empresa" sortable />
        <Column field="companyLocation" header="Localização" sortable />
        <Column
          field="listedDate"
          header="Data de Publicação"
          body={(row) => new Date(row.listedDate).toLocaleDateString()}
          sortable
        />
        <Column field="experienceLevel" header="Experiência" sortable />
        <Column body={linkTemplate} header="LinkedIn" style={{ width: "8rem" }} />
      </DataTable>
    </div>
  );
};

const LinkedinCardsRanking: React.FC = () => {
  const [dimensions, setDimensions] = useState<string[]>(["country"]);
  const [detailsDialogVisible, setDetailsDialogVisible] = useState(false);
  const [selectedRowForDetails, setSelectedRowForDetails] = useState<any>(null);
  const [lazyParams, setLazyParams] = useState({
    page: 1,
    limit: 10,
  });

  // Estados dos filtros
  const [selectedCompanies, setSelectedCompanies] = useState<string>("");
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<Nullable<(Date | null)[]>>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return [start, end];
  });

  const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  });

  // Opções carregadas dinamicamente
  const { data: filterOptionsRes, isLoading: isLoadingOptions } = useLinkedInCardsFilterOptions();
  const filterOptions = filterOptionsRes?.data || { companies: [], positions: [], titles: [], countries: [], cities: [] };

  const dimensionOptions = [
    { label: "Empresa", value: "companyName" },
    { label: "Vaga Pesquisada", value: "position" },
    { label: "País", value: "country" },
    { label: "Ano", value: "year" },
    { label: "Mês", value: "month" },
  ];

  const labelMap: Record<string, string> = {
    companyName: "Empresa",
    position: "Vaga Pesquisada",
    country: "País",
    year: "Ano",
    month: "Mês",
  };

  const handleDimensionChange = (e: any) => {
    setDimensions(e.value);
    setLazyParams((prev) => ({ ...prev, page: 1 }));
  };

  const handleCountryDrilldown = (countryName: string) => {
    setSelectedCountries([countryName]);
    const newFilters = { ...activeFilters, country: countryName };
    setActiveFilters(newFilters);
    setDimensions(["companyName"]);
    setLazyParams((prev) => ({ ...prev, page: 1 }));
  };

  const handleApplyFilters = () => {
    const cleaned: Record<string, any> = {};

    if (selectedCompanies.trim()) {
      cleaned.companyName = selectedCompanies.trim();
    }
    if (selectedPositions.length > 0) {
      cleaned.position = selectedPositions.join(",");
    }
    if (selectedCountries.length > 0) {
      cleaned.country = selectedCountries.join(",");
    }

    if (dateRange && dateRange[0]) {
      cleaned.startDate = dateRange[0].toISOString();
    }
    if (dateRange && dateRange[1]) {
      cleaned.endDate = dateRange[1].toISOString();
    }

    setActiveFilters(cleaned);
    setLazyParams((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSelectedCompanies("");
    setSelectedPositions([]);
    setSelectedCountries([]);
    setDateRange(null);

    setActiveFilters({});
    setLazyParams((prev) => ({ ...prev, page: 1 }));
  };

  // Faz a query para o ranking passando as dimensões agrupadoras e os filtros ativos
  const groupByQuery = dimensions.join(",");
  const { data: rankingRes, isLoading } = useLinkedInCardsRanking(
    groupByQuery,
    lazyParams.page,
    lazyParams.limit,
    activeFilters
  );

  const rankingData = rankingRes?.data || [];
  const totalRecords = rankingRes?.pagination?.count || 0;

  const onPage = (event: any) => {
    setLazyParams((prev) => ({
      ...prev,
      page: (event.page ?? 0) + 1,
      limit: event.rows ?? 10,
    }));
  };


  return (
    <div className="grid">
      {/* Secção de Filtros / Dimensões */}
      <div className="col-12 md:col-3">
        <Card title="Agrupar e Analisar por" className="h-full">
          <div className="flex flex-column gap-3">
            <MultiSelect
              value={dimensions}
              options={dimensionOptions}
              onChange={handleDimensionChange}
              placeholder="Selecionar Dimensões"
              display="chip"
              className="w-full p-inputtext-sm"
            />
          </div>

          {/* Filtros Adicionais com MultiSelects */}
          <div className="border-top-1 border-300 pt-3 mt-3">
            <h4 className="m-0 mb-3 text-lg font-bold">Filtros de Pesquisa</h4>
            <div className="flex flex-column gap-3">
              <div className="flex flex-column gap-1">
                <label className="font-medium text-sm">Empresa</label>
                <InputText
                  value={selectedCompanies}
                  onChange={(e) => setSelectedCompanies(e.target.value)}
                  placeholder="Pesquisar Empresa (contém)..."
                  className="w-full p-inputtext-sm"
                />
              </div>

              <div className="flex flex-column gap-1">
                <label className="font-medium text-sm">Vaga Pesquisada</label>
                <MultiSelect
                  value={selectedPositions}
                  options={filterOptions.positions}
                  onChange={(e) => setSelectedPositions(e.value)}
                  placeholder={isLoadingOptions ? "Carregando..." : "Selecionar Vagas"}
                  filter
                  className="w-full p-inputtext-sm"
                  maxSelectedLabels={2}
                  disabled={isLoadingOptions}
                />
              </div>

              <div className="flex flex-column gap-1">
                <label className="font-medium text-sm">País</label>
                <MultiSelect
                  value={selectedCountries}
                  options={filterOptions.countries}
                  onChange={(e) => setSelectedCountries(e.value)}
                  placeholder={isLoadingOptions ? "Carregando..." : "Selecionar Países"}
                  filter
                  className="w-full p-inputtext-sm"
                  maxSelectedLabels={2}
                  disabled={isLoadingOptions}
                />
              </div>

              <div className="flex flex-column gap-1">
                <label className="font-medium text-sm">Intervalo de Datas</label>
                <Calendar
                  value={dateRange}
                  onChange={(e) => setDateRange(e.value)}
                  selectionMode="range"
                  readOnlyInput
                  hideOnRangeSelection
                  placeholder="Selecionar Período"
                  className="w-full p-inputtext-sm"
                  dateFormat="dd/mm/yy"
                  showIcon
                />
              </div>

              <div className="flex gap-2 mt-2">
                <Button label="Filtrar" icon="pi pi-filter" className="p-button-sm flex-1" onClick={handleApplyFilters} />
                <Button label="Limpar" icon="pi pi-filter-slash" severity="secondary" className="p-button-sm p-button-outlined" onClick={handleClearFilters} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Secção da Tabela Dinâmica */}
      <div className="col-12 md:col-9">
        <div className="card">
          <DataTable
            value={rankingData}
            lazy
            paginator
            rows={lazyParams.limit}
            first={(lazyParams.page - 1) * lazyParams.limit}
            totalRecords={totalRecords}
            onPage={onPage}
            loading={isLoading}
            dataKey={(data) => dimensions.map((d) => data[d] || "").join("-")}
            responsiveLayout="scroll"
            emptyMessage="Nenhum registro encontrado com estas dimensões/filtros."
            className="shadow-1 border-round"
          >
            {dimensions.map((dim) => (
              <Column
                key={dim}
                field={dim}
                header={labelMap[dim]}
                body={(rowData) => {
                  const val = rowData[dim];
                  if (dim === "month" && val != null) {
                    const months = [
                      "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
                      "Jul", "Ago", "Set", "Out", "Nov", "Dez"
                    ];
                    return months[Number(val) - 1] || val;
                  }
                  if (dim === "country" && val != null) {
                    return (
                      <button
                        type="button"
                        className="p-link font-bold text-blue-600 hover:underline align-left text-left"
                        onClick={() => handleCountryDrilldown(val)}
                      >
                        {val}
                      </button>
                    );
                  }
                  return val != null ? String(val) : "Não especificado";
                }}
              />
            ))}
            <Column
              field="count"
              header="Quantidade de Vagas"
              body={(row) => <span className="font-bold text-lg">{row.count}</span>}
              style={{ width: "12rem", textAlign: "right" }}
            />
            <Column
              body={(rowData) => (
                <Button
                  icon="pi pi-search"
                  label="Ver Vagas"
                  className="p-button-outlined p-button-sm font-semibold"
                  onClick={() => {
                    setSelectedRowForDetails(rowData);
                    setDetailsDialogVisible(true);
                  }}
                />
              )}
              header="Ações"
              style={{ width: "10rem", textAlign: "center" }}
            />
          </DataTable>
        </div>
      </div>

      <Dialog
        header="Detalhamento das Vagas"
        visible={detailsDialogVisible}
        style={{ width: "75vw" }}
        onHide={() => {
          setDetailsDialogVisible(false);
          setSelectedRowForDetails(null);
        }}
        maximizable
        modal
        className="p-fluid"
      >
        {selectedRowForDetails && (
          <LinkedinCardsGroupDetails
            rowData={selectedRowForDetails}
            activeDimensions={dimensions}
          />
        )}
      </Dialog>
    </div>
  );
};

export default LinkedinCardsRanking;
